/**
 * Minimal IndexedDB wrapper for the Novatus offline queue.
 * Two object stores:
 *  - pending_ops  : queue of mutating operations to replay when back online
 *  - cache        : key/value store for cached GET responses (read-only access offline)
 */

const DB_NAME = "novatus_offline";
const DB_VERSION = 2;
const STORE_OPS = "pending_ops";
const STORE_CACHE = "cache";
const STORE_PHOTOS = "pending_photos";

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB nicht verfügbar"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_OPS)) {
        db.createObjectStore(STORE_OPS, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(STORE_PHOTOS)) {
        db.createObjectStore(STORE_PHOTOS, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(store, mode = "readonly") {
  return openDB().then((db) => db.transaction(store, mode).objectStore(store));
}

function reqAsPromise(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/* ---------- pending_ops API ---------- */

export async function addPendingOp(op) {
  const store = await tx(STORE_OPS, "readwrite");
  return reqAsPromise(store.put(op));
}

export async function getAllPendingOps() {
  const store = await tx(STORE_OPS);
  return reqAsPromise(store.getAll());
}

export async function deletePendingOp(id) {
  const store = await tx(STORE_OPS, "readwrite");
  return reqAsPromise(store.delete(id));
}

export async function countPendingOps() {
  const store = await tx(STORE_OPS);
  return reqAsPromise(store.count());
}

/* ---------- cache API (used for offline reads of project list / detail) ---------- */

export async function cacheSet(key, value) {
  const store = await tx(STORE_CACHE, "readwrite");
  return reqAsPromise(store.put({ key, value, at: Date.now() }));
}

export async function cacheGet(key) {
  const store = await tx(STORE_CACHE);
  const row = await reqAsPromise(store.get(key));
  return row ? row.value : null;
}

/* ---------- pending_photos API (stores Blobs for offline photo uploads) ---------- */

export async function addPendingPhoto(row) {
  const store = await tx(STORE_PHOTOS, "readwrite");
  return reqAsPromise(store.put(row));
}

export async function getAllPendingPhotos() {
  const store = await tx(STORE_PHOTOS);
  return reqAsPromise(store.getAll());
}

export async function deletePendingPhoto(id) {
  const store = await tx(STORE_PHOTOS, "readwrite");
  return reqAsPromise(store.delete(id));
}

export async function countPendingPhotos() {
  const store = await tx(STORE_PHOTOS);
  return reqAsPromise(store.count());
}
